import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { CircleXIcon } from 'images'

const formatCredits = (value, language) => {
  if (value == null) return null
  return Number(value).toLocaleString(language)
}

export default function CoCreateCredits({
  creditStatus,
  error,
  onRetry,
  showSummary,
  totalCost,
  hasInsufficientCredits,
}) {
  const { i18n, t } = useTranslation()
  const creditAccess = creditStatus?.credit_access?.state
  const credits = creditStatus?.credits ?? {}
  const billingPortalUrl = creditStatus?.billing_portal_url ?? 'https://billing.future-demand.com'
  const termsBlocked = creditAccess === 'v4_acceptance_required'
  const remaining = credits.remaining
  const language = i18n?.language ?? 'en'

  if (!showSummary) return null

  if (termsBlocked) {
    return (
      <div className="co-create-credit-notice" role="alert">
          <CircleXIcon width={15} height={15} />

        <div className="co-create-credit-notice-message">
          <div>
            <p className="notice-title">
              {t('Recommendations.campaign.setup.coCreatePanel.credits.termsTitle')}
            </p>

            <p>{t('Recommendations.campaign.setup.coCreatePanel.credits.termsDescription')}</p>
          </div>

          <a
            href={billingPortalUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => event.stopPropagation()}
            >
            {t('Recommendations.campaign.setup.coCreatePanel.credits.openBilling')}
          </a>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="co-create-credit-notice" role="alert">
          <CircleXIcon width={15} height={15} />

        <div className="co-create-credit-notice-message">
          <div>
            <p className="notice-title">
              {t('Recommendations.campaign.setup.coCreatePanel.credits.unavailableTitle')}
            </p>

            <p>{t('Recommendations.campaign.setup.coCreatePanel.credits.unavailableDescription')}</p>
          </div>

          <button
            type="button"
            className="credit-retry-button"
            onClick={(event) => {
              event.stopPropagation()
              onRetry?.()
            }}
            >
            {t('common.retry')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="co-create-credit-area" aria-live="polite">
      {hasInsufficientCredits && (
        <div className="co-create-credit-notice" role="alert">
            <CircleXIcon width={15} height={15} />

          <div className="co-create-credit-notice-message">
            <div>
              <p className="notice-title">
                {t('Recommendations.campaign.setup.coCreatePanel.credits.insufficientTitle')}
              </p>

              <p>
                {t('Recommendations.campaign.setup.coCreatePanel.credits.insufficientDescription', {
                  required: formatCredits(totalCost, language),
                  available: formatCredits(remaining, language),
                })}
              </p>
            </div>


            <a
              href={billingPortalUrl}
              target="_blank"
              rel="noreferrer"
              onClick={(event) => event.stopPropagation()}
              >
              {t('Recommendations.campaign.setup.coCreatePanel.credits.openBilling')}
            </a>
          </div>
        </div>
      )}
    </div>
  )
}

CoCreateCredits.propTypes = {
  creditStatus: PropTypes.shape({
    billing_portal_url: PropTypes.string,
    credit_access: PropTypes.shape({ state: PropTypes.string }),
    credits: PropTypes.shape({
      cost_per_image: PropTypes.number,
      remaining: PropTypes.number,
    }),
  }),
  error: PropTypes.string,
  imageCount: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  onRetry: PropTypes.func,
  showSummary: PropTypes.bool,
  totalCost: PropTypes.number.isRequired,
}
